import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ZoomIn } from "lucide-react";
import { Eyebrow, Reveal } from "./Reveal";
import { firstWorks } from "../data/business";

export default function RealWork() {
    const [active, setActive] = useState(null);
    const [main, ...stack] = firstWorks;

    return (
        <section
            id="trabalhos"
            data-testid="real-work-section"
            className="bg-paper py-20 sm:py-28 lg:py-32"
        >
            <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
                <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
                    <div>
                        <Eyebrow>Trabalhos reais</Eyebrow>
                        <Reveal delay={0.1}>
                            <h2 className="mt-5 font-display text-3xl font-medium uppercase leading-[1.08] tracking-tight text-ink sm:text-4xl lg:text-5xl">
                                Cada detalhe
                                <br />
                                <span className="italic text-accent">faz parte do resultado.</span>
                            </h2>
                        </Reveal>
                    </div>
                    <Reveal delay={0.2}>
                        <p className="max-w-sm text-sm text-ink-soft sm:text-base">
                            Conheça alguns dos trabalhos realizados no Studio Danielly
                            Sousa.
                        </p>
                    </Reveal>
                </div>

                <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:gap-8">
                    <Reveal className="h-full">
                        <button
                            data-testid="realwork-main"
                            onClick={() => setActive(main)}
                            className="group relative block h-full w-full overflow-hidden text-left"
                        >
                            <div className={`${main.ratio} md:h-[640px] w-full overflow-hidden md:aspect-auto`}>
                                <img
                                    src={main.url}
                                    alt={main.title}
                                    loading="lazy"
                                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                                />
                            </div>
                            <span className="absolute left-5 top-5 border border-paper/60 bg-ink/30 px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.25em] text-paper backdrop-blur-sm">
                                {main.tag}
                            </span>
                            <ZoomIn size={18} className="absolute bottom-5 right-5 text-paper/90" />
                        </button>
                    </Reveal>

                    <div className="grid grid-cols-1 gap-6 lg:gap-8">
                        {stack.map((w, i) => (
                            <Reveal key={w.title} delay={0.12 + i * 0.12}>
                                <button
                                    data-testid={`realwork-${i + 2}`}
                                    onClick={() => setActive(w)}
                                    className="group relative block w-full overflow-hidden text-left"
                                >
                                    <div className={`${w.ratio} w-full overflow-hidden`}>
                                        <img
                                            src={w.url}
                                            alt={w.title}
                                            loading="lazy"
                                            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                                        />
                                    </div>
                                    <span className="absolute left-5 top-5 border border-paper/60 bg-ink/30 px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.25em] text-paper backdrop-blur-sm">
                                        {w.tag}
                                    </span>
                                    <ZoomIn size={18} className="absolute bottom-5 right-5 text-paper/90" />
                                </button>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </div>

            <AnimatePresence>
                {active && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[70] flex items-center justify-center bg-ink/95 p-4 sm:p-10"
                        onClick={() => setActive(null)}
                    >
                        <button
                            data-testid="realwork-lightbox-close"
                            aria-label="Fechar"
                            onClick={() => setActive(null)}
                            className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center border border-paper/25 text-paper transition-colors hover:border-accent hover:text-accent"
                        >
                            <X size={20} />
                        </button>
                        <motion.img
                            initial={{ scale: 0.94, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.96, opacity: 0 }}
                            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                            src={active.url}
                            alt={active.title}
                            onClick={(e) => e.stopPropagation()}
                            className="max-h-[80vh] w-auto max-w-full object-contain"
                        />
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
}
